import type { ReactNode } from 'react';

export type SampleManagerPageHeadingProps = {
  /** 互換用（可視は常に fullLabel） */
  shortLabel: string;
  fullLabel: string;
  /**
   * true: 全幅でフル名を可視（任意アイコン）。
   * false: xs のみ可視フル名、md+ は visually-hidden（タブと重複回避）。
   */
  showFullOnMd?: boolean;
  /**
   * true: 見出しは常に visually-hidden（ナビ短名と重複する一覧向け）。
   * 文書構造の h1 は残す。
   */
  srOnly?: boolean;
  iconClassName?: string;
  /** 外側ラッパ（showFullOnMd 時）または xs h1 の追加クラス */
  className?: string;
  /** 見出しテキスト span の追加クラス（例: h3） */
  fullClassName?: string;
  /** 見出し右に置く（ヘルプ等）。showFullOnMd=false でも md+ に残す */
  trailing?: ReactNode;
};

/**
 * サンプル管理系ページの h1。
 * ナビ短名との二重表示を避けるため、可視テキストは常に fullLabel（srOnly 時を除く）。
 */
export function SampleManagerPageHeading({
  shortLabel: _shortLabel,
  fullLabel,
  showFullOnMd = false,
  srOnly = false,
  iconClassName,
  className,
  fullClassName,
  trailing,
}: SampleManagerPageHeadingProps) {
  const wrapClass = `d-flex justify-content-between align-items-start flex-wrap gap-2 ${className ?? 'mb-2'}`;

  if (srOnly) {
    return (
      <>
        <h1 className="visually-hidden">{fullLabel}</h1>
        {trailing ? <div className={wrapClass}>{trailing}</div> : null}
      </>
    );
  }

  if (showFullOnMd) {
    return (
      <div className={wrapClass}>
        <h1 className="h4 mb-0 d-flex align-items-center min-w-0">
          {iconClassName ? (
            <i
              className={`${iconClassName} me-2 text-primary d-none d-md-inline`}
              aria-hidden="true"
            />
          ) : null}
          <span className={fullClassName}>{fullLabel}</span>
        </h1>
        {trailing}
      </div>
    );
  }

  // xs: セクション select は短名のため、ページ h1 はフル名（「一覧」二重表示を避ける）
  // md+: タブと重複する見出しは隠し、trailing があれば右寄せで残す
  if (!trailing) {
    return (
      <>
        <h1 className={`h4 mb-2 d-md-none ${fullClassName ?? ''} ${className ?? ''}`.trim()}>
          {fullLabel}
        </h1>
        <p className="visually-hidden d-none d-md-block mb-0">{fullLabel}</p>
      </>
    );
  }

  return (
    <div className={wrapClass}>
      <h1 className={`h4 mb-0 d-md-none ${fullClassName ?? ''}`.trim()}>{fullLabel}</h1>
      <p className="visually-hidden d-none d-md-block mb-0">{fullLabel}</p>
      <div className="ms-md-auto flex-shrink-0">{trailing}</div>
    </div>
  );
}
